// A regra que é NOSSA nas calculadoras: montar o pedido e ler a resposta.
//
// Módulo puro, com prova própria (`app/tests/calculadoras.mjs`). O que ele
// **não** faz é calcular tributo: isso é da Receita, e reescrever aqui criaria
// a segunda definição do mesmo número. O que ele faz é o trabalho de fronteira
// — traduzir um formulário em ROC, decidir quando um campo é obrigatório, e
// separar "resposta zerada porque a operação é isenta" de "resposta zerada
// porque o fornecedor não está cadastrado no piloto".
//
// Ele não importa NADA: a leitura dos números do gov mora aqui e não em
// `calculadoras.ts` porque a dependência tem de apontar do IO para a regra, e
// nunca ao contrário — um módulo puro que importasse o cliente do Supabase não
// poderia ser provado sem navegador.

// ---------------------------------------------------------------------------
// Ler os números que o gov manda
// ---------------------------------------------------------------------------
/**
 * O gov manda número ora como NÚMERO, ora como STRING — e isso não é descuido
 * dele: no ROC os valores monetários vêm com casas fixas (`"1000.00"`,
 * `"0.90"`) porque a precisão é do documento fiscal, e nas bases de cálculo vêm
 * como `637.5`. Medido nas duas respostas em 14/09.
 *
 * A conversão é sempre com PONTO decimal, nunca `toLocaleString` ao contrário:
 * é o formato da API, e tratá-lo como brasileiro leria "1.000,00" onde está
 * escrito "1000.00".
 */
export function numeroDoGov(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Reais para a tela. Nulo vira travessão, nunca "R$ 0,00" — zero é um valor. */
export function emReais(v: unknown): string {
  const n = numeroDoGov(v);
  if (n === null) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Percentual para a tela, com as casas que o gov mandou. */
export function emPercentual(v: unknown): string {
  const n = numeroDoGov(v);
  if (n === null) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + "%";
}

// ---------------------------------------------------------------------------
// A data do fato gerador
// ---------------------------------------------------------------------------
/**
 * O `<input type="date">` entrega `2026-06-01`; o gov quer
 * `yyyy-MM-dd'T'HH:mm:ssXXX`.
 *
 * A hora é MEIO-DIA, e o fuso é o de Brasília. Não é detalhe: o que o gov usa
 * do carimbo é a DATA (a vigência da alíquota, da classificação tributária e do
 * Imposto Seletivo muda por dia), e meia-noite em -03:00 vira o dia anterior em
 * qualquer fuso a oeste. Meio-dia não vira o dia em nenhum fuso do Brasil, nem
 * em nenhum navegador com o relógio mal configurado.
 */
export function fatoGeradorDoDia(dia: string): string {
  return `${dia}T12:00:00-03:00`;
}

/** O ano do fato gerador, do texto do campo. Nulo quando a data não serve. */
export function anoDoDia(dia: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia.trim());
  if (!m) return null;
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  const d = Number(m[3]);
  if (mes < 1 || mes > 12 || d < 1 || d > 31) return null;
  return ano;
}

// ---------------------------------------------------------------------------
// As alíquotas nominais — obrigatórias a partir de 2027
// ---------------------------------------------------------------------------
/**
 * A partir de 01/01/2027 o gov EXIGE as três alíquotas nominais no item, e
 * recusa o cálculo sem elas (422 "As alíquotas nominais (os três campos cbs,
 * ibsEstadual e ibsMunicipal) devem ser informadas…", medido em 14/09).
 *
 * E as de referência não existem para consultar: `aliquota-uniao?data=2027-06-01`
 * responde **404** — "não encontrada para o tributo com id 2". Ou seja, não há
 * como o hub preencher sozinho com número oficial, porque número oficial não
 * há. Quem informa é o aluno.
 */
export function exigeAliquotasNominais(dia: string): boolean {
  const ano = anoDoDia(dia);
  return ano !== null && ano >= 2027;
}

/**
 * A sugestão que a tela oferece para 2027 em diante — e a procedência dela, que
 * vai para a tela junto do número.
 *
 * **CBS 8,8% e IBS 17,7%** é a estimativa de alíquota de referência divulgada
 * pelo Ministério da Fazenda (26,5% no total). Não é lei: a fixação sai por lei
 * ordinária e por resolução do Senado, e enquanto isso não acontece o próprio
 * piloto responde 404 quando se pergunta a ele.
 *
 * **A repartição do IBS entre estado e município é NOSSA e é arbitrária.** Os
 * 17,7% são do IBS inteiro; quanto vai para cada ente depende de resolução do
 * Senado, que ainda não existe. Repartimos 80/20 para a tela não nascer com o
 * município zerado — o que ensinaria errado —, e a tela DIZ que esse corte é
 * nosso e que o aluno deve ajustar. Número inventado com cara de oficial é o
 * defeito que esta casa já pagou (a explicação do teto do MEI calculada por
 * fora, que contradizia o número em quatro situações); número inventado com a
 * procedência ao lado é uma sugestão.
 *
 * A resposta do gov a um cálculo com estes valores vem com
 * `calculoSimulado: true` e o cabeçalho `x-warning-dados-simulados: 6` — a tela
 * mostra o selo, então o aluno nunca confunde isto com cálculo definitivo.
 */
export const SUGESTAO_2027 = {
  cbs: 8.8,
  ibsEstadual: 14.16,
  ibsMunicipal: 3.54,
} as const;

export const FONTE_DA_SUGESTAO =
  "CBS 8,8% e IBS 17,7% são a estimativa de alíquota de referência do Ministério da Fazenda " +
  "(26,5% no total) — ainda não fixada em lei. A divisão do IBS entre estado (80%) e município " +
  "(20%) é uma sugestão nossa: a repartição depende de resolução do Senado. Ajuste os três.";

// ---------------------------------------------------------------------------
// O ROC do Regime Geral
// ---------------------------------------------------------------------------
export type ItemDaTela = {
  /** "ncm" para mercadoria, "nbs" para serviço. */
  natureza: "ncm" | "nbs";
  codigo: string;
  cst: string;
  cClassTrib: string;
  baseCalculo: number | null;
  quantidade: number | null;
  unidade: string;
  /** O grupo do Imposto Seletivo, quando o NCM exige. */
  is: { cst: string; cClassTrib: string; baseCalculo: number | null; adValorem: number | null } | null;
  aliquotas: { cbs: number | null; ibsEstadual: number | null; ibsMunicipal: number | null } | null;
};

export type FormularioRegimeGeral = {
  dia: string;
  uf: string;
  municipio: number | null;
  tpDoc: number | null;
  /** Compra governamental, quando o aluno ligou o grupo. */
  compraGov: { tpEnteGov: number; tpOperGov: number | null } | null;
  item: ItemDaTela;
};

/** O que impede o envio, em português de quem preenche. Vazio = pode enviar. */
export function faltaNoRegimeGeral(f: FormularioRegimeGeral): string[] {
  const falta: string[] = [];
  if (anoDoDia(f.dia) === null) falta.push("a data do fato gerador");
  if (!f.uf) falta.push("a UF");
  if (!f.municipio) falta.push("o município");
  if (!f.tpDoc) falta.push("o tipo de documento fiscal");
  if (!f.item.codigo.trim()) {
    falta.push(f.item.natureza === "ncm" ? "o código NCM" : "o código NBS");
  }
  if (!f.item.cst) falta.push("a situação tributária (CST)");
  if (!f.item.cClassTrib) falta.push("a classificação tributária");
  if (f.item.baseCalculo === null || f.item.baseCalculo <= 0) falta.push("a base de cálculo");

  // As nominais só entram na lista quando o gov as exige — cobrá-las em 2026
  // seria pedir ao aluno um número que a API já tem.
  if (exigeAliquotasNominais(f.dia)) {
    const a = f.item.aliquotas;
    if (a === null || a.cbs === null || a.ibsEstadual === null || a.ibsMunicipal === null) {
      falta.push("as três alíquotas nominais (obrigatórias a partir de 2027)");
    }
  }

  if (f.item.is) {
    if (f.item.is.baseCalculo === null || f.item.is.baseCalculo <= 0) {
      falta.push("a base de cálculo do Imposto Seletivo");
    }
    if (!f.item.is.cst) falta.push("a CST do Imposto Seletivo");
    if (!f.item.is.cClassTrib) falta.push("a classificação tributária do Imposto Seletivo");
  }

  return falta;
}

/**
 * O formulário vira o ROC que o gov espera.
 *
 * O `id` e a `versao` são NOSSOS, não do aluno: o `id` identifica o pedido nos
 * logs do gov e a `versao` é a do layout do ROC. Deixá-los no formulário daria
 * ao aluno um campo que ele não sabe preencher e que nada faz por ele.
 *
 * Campo vazio não vai como `null` — ele **não vai**. Um `null` num campo
 * opcional do ROC é diferente de ausência para o validador do gov, e foi o que
 * fez o Bubble receber 422 em cenários que deveriam passar.
 */
export function montarRoc(f: FormularioRegimeGeral): Record<string, unknown> {
  const item: Record<string, unknown> = {
    numero: 1,
    cst: f.item.cst,
    cClassTrib: f.item.cClassTrib,
  };
  if (f.item.natureza === "ncm") item.ncm = f.item.codigo.replace(/\D/g, "");
  else item.nbs = f.item.codigo.replace(/\D/g, "");
  if (f.item.baseCalculo !== null) item.baseCalculo = f.item.baseCalculo;
  if (f.item.quantidade !== null) item.quantidade = f.item.quantidade;
  if (f.item.unidade.trim()) item.unidade = f.item.unidade.trim().toUpperCase();

  if (f.item.is) {
    const is: Record<string, unknown> = {
      cst: f.item.is.cst,
      cClassTrib: f.item.is.cClassTrib,
    };
    if (f.item.is.baseCalculo !== null) is.baseCalculo = f.item.is.baseCalculo;
    if (f.item.quantidade !== null) is.quantidade = f.item.quantidade;
    if (f.item.unidade.trim()) is.unidade = f.item.unidade.trim().toUpperCase();
    item.impostoSeletivo = is;
  }

  const a = f.item.aliquotas;
  if (exigeAliquotasNominais(f.dia) && a && a.cbs !== null && a.ibsEstadual !== null && a.ibsMunicipal !== null) {
    const nominais: Record<string, unknown> = {
      cbs: a.cbs,
      ibsEstadual: a.ibsEstadual,
      ibsMunicipal: a.ibsMunicipal,
    };
    // A alíquota do IS só entra quando há grupo de IS — mandá-la sozinha faz o
    // gov recusar o item por informar alíquota de um tributo que a operação não
    // tem.
    if (f.item.is && f.item.is.adValorem !== null) {
      nominais.impostoSeletivo = { adValorem: f.item.is.adValorem };
    }
    item.aliquotasNominais = nominais;
  }

  const roc: Record<string, unknown> = {
    id: identificadorDoRoc(),
    versao: "0.0.1",
    dhFatoGerador: fatoGeradorDoDia(f.dia),
    municipio: f.municipio,
    uf: f.uf,
    tpDoc: f.tpDoc,
    itens: [item],
  };

  if (f.compraGov) {
    const g: Record<string, unknown> = { tpEnteGov: f.compraGov.tpEnteGov };
    if (f.compraGov.tpOperGov !== null) g.tpOperGov = f.compraGov.tpOperGov;
    roc.gCompraGov = g;
  }

  return roc;
}

/**
 * Um identificador por pedido, para o gov e para os nossos logs.
 *
 * `crypto.randomUUID()` sem os hifens — o campo do gov é um texto livre, e o
 * exemplo dele tem 32 caracteres hexadecimais. Não é segredo nem chave: serve
 * para casar um pedido com uma resposta quando alguém for investigar.
 */
function identificadorDoRoc(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

// ---------------------------------------------------------------------------
// Ler a resposta do Regime Geral
// ---------------------------------------------------------------------------
export type LinhaDeTributo = { rotulo: string; aliquota: number | null; valor: number | null; memoria: string | null };

export type ResultadoRegimeGeral = {
  baseCalculo: number | null;
  linhas: LinhaDeTributo[];
  /** IBS + CBS (+ IS, quando há). O que o aluno soma de imposto. */
  totalTributos: number | null;
  impostoSeletivo: LinhaDeTributo | null;
  /** O redutor da compra governamental, quando o grupo foi enviado. */
  redutorCompraGov: number | null;
  /** O gov marcou o item como calculado com alíquota informada pelo aluno. */
  simuladoNoItem: boolean;
};

type Qualquer = Record<string, unknown>;
function obj(v: unknown): Qualquer | null {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Qualquer) : null;
}

export function lerRegimeGeral(dados: unknown): ResultadoRegimeGeral {
  const raiz = obj(dados) ?? {};
  const objetos = Array.isArray(raiz.objetos) ? raiz.objetos : [];
  const primeiro = obj(objetos[0]) ?? {};
  const trib = obj(primeiro.tribCalc) ?? {};
  const ibscbs = obj(trib.IBSCBS) ?? {};
  const g = obj(ibscbs.gIBSCBS) ?? {};
  const uf = obj(g.gIBSUF) ?? {};
  const mun = obj(g.gIBSMun) ?? {};
  const cbs = obj(g.gCBS) ?? {};
  const is = obj(trib.IS);

  const linhas: LinhaDeTributo[] = [
    {
      rotulo: "IBS Estadual",
      aliquota: numeroDoGov(uf.pIBSUF),
      valor: numeroDoGov(uf.vIBSUF),
      memoria: typeof uf.memoriaCalculo === "string" ? uf.memoriaCalculo : null,
    },
    {
      rotulo: "IBS Municipal",
      aliquota: numeroDoGov(mun.pIBSMun),
      valor: numeroDoGov(mun.vIBSMun),
      memoria: typeof mun.memoriaCalculo === "string" ? mun.memoriaCalculo : null,
    },
    {
      rotulo: "CBS",
      aliquota: numeroDoGov(cbs.pCBS),
      valor: numeroDoGov(cbs.vCBS),
      memoria: typeof cbs.memoriaCalculo === "string" ? cbs.memoriaCalculo : null,
    },
  ];

  const linhaIs: LinhaDeTributo | null = is
    ? {
      rotulo: "Imposto Seletivo",
      aliquota: numeroDoGov(is.pIS),
      valor: numeroDoGov(is.vIS),
      memoria: typeof is.memoriaCalculo === "string" ? is.memoriaCalculo : null,
    }
    : null;

  // A soma é das PARTES que vieram, e não de um campo de total do gov: o total
  // dele (`IBSCBSTot`) é da operação inteira e existe para nota com vários
  // itens. Com um item só os dois batem; somar aqui mantém o número coerente
  // com as linhas que estão na tela logo acima dele, que é o que o aluno confere.
  const partes = [...linhas.map((l) => l.valor), linhaIs?.valor ?? null].filter(
    (v): v is number => v !== null,
  );
  const totalTributos = partes.length > 0 ? partes.reduce((a, b) => a + b, 0) : null;

  const oper = obj(raiz.oper) ?? {};
  const gov = obj(oper.gCompraGov);

  return {
    baseCalculo: numeroDoGov(g.vBC),
    linhas,
    totalTributos,
    impostoSeletivo: linhaIs,
    redutorCompraGov: gov ? numeroDoGov(gov.pRedutor) : null,
    simuladoNoItem: primeiro.calculoSimulado === true,
  };
}

// ---------------------------------------------------------------------------
// Ler a resposta do Split Payment Simplificado
// ---------------------------------------------------------------------------
export type ResultadoSplit = {
  aliquotaCbs: number | null;
  aliquotaIbsUf: number | null;
  aliquotaIbsMun: number | null;
  valorCbs: number | null;
  valorIbsUf: number | null;
  valorIbsMun: number | null;
  valorTotalTributos: number | null;
  valorFornecedor: number | null;
  /**
   * O fornecedor não tem percentual cadastrado no piloto.
   *
   * O gov responde **HTTP 200 com tudo zerado** nesse caso — sem erro, sem
   * aviso. Medido em 14/09 com o CNPJ que o Bubble usava (`5F5CNBWQ402521`):
   * `{"valorCbs":"0.00", …, "valorFornecedor":"20568.86"}`. Sem esta leitura a
   * tela mostraria "R$ 0,00 de CBS" como se fosse resultado — número errado com
   * cara de certo, que é o pior desfecho possível numa calculadora.
   */
  semCadastro: boolean;
};

export function lerSplit(dados: unknown, baseEnviada: number | null): ResultadoSplit {
  const d = obj(dados) ?? {};
  const r: ResultadoSplit = {
    aliquotaCbs: numeroDoGov(d.aliquotaCbs),
    aliquotaIbsUf: numeroDoGov(d.aliquotaIbsUf),
    aliquotaIbsMun: numeroDoGov(d.aliquotaIbsMun),
    valorCbs: numeroDoGov(d.valorCbs),
    valorIbsUf: numeroDoGov(d.valorIbsUf),
    valorIbsMun: numeroDoGov(d.valorIbsMun),
    valorTotalTributos: numeroDoGov(d.valorTotalTributos),
    valorFornecedor: numeroDoGov(d.valorFornecedor),
    semCadastro: false,
  };

  // A distinção: total ZERO com base POSITIVA. Uma base zero não chega aqui (o
  // gov exige `minimum: 0.01`), e um fornecedor cadastrado com alíquota real
  // nunca produz retenção zero sobre base positiva. É a única combinação que
  // significa "não achei este CPF/CNPJ".
  const base = baseEnviada ?? r.valorFornecedor;
  r.semCadastro = (r.valorTotalTributos ?? 0) === 0 && base !== null && base > 0;
  return r;
}

/** Uma linha do cadastro de Split Simplificado, como o gov devolve. */
export type CadastroSplit = {
  cpfCnpj: string;
  aliquotaCbs: number;
  aliquotaIbsUf: number;
  aliquotaIbsMun: number;
  inicioVigencia: string;
};

/**
 * O cadastro do Split AGRUPADO por fornecedor, com o percentual da data
 * escolhida.
 *
 * A lista do gov é uma linha por (fornecedor, vigência): medido em 14/09, são
 * **31 linhas para 19 fornecedores** — quatro deles aparecem quatro vezes, uma
 * por ano de 2027 a 2030. Mostrada crua, a tabela repete o mesmo CNPJ quatro
 * vezes com quatro botões "Usar" e o aluno não tem como saber qual clicar; é a
 * cicatriz do "conte quantas vezes a mesma informação aparece", que já levou o
 * IR Pro de 8.249px para 1.120px com o MESMO conteúdo.
 *
 * A vigência que vale é a MAIOR que já começou na data escolhida — a mesma
 * precedência do gov ao calcular. Quando nenhuma começou ainda, devolvemos a
 * primeira e dizemos a partir de quando ela vale: esconder o fornecedor faria a
 * lista encolher ao trocar a data, e o aluno concluiria que ele sumiu.
 */
export function porFornecedor(
  cadastro: CadastroSplit[],
  dia: string,
): Array<CadastroSplit & { vigenteEm: string | null; periodos: number }> {
  const grupos = new Map<string, CadastroSplit[]>();
  for (const c of cadastro) {
    const lista = grupos.get(c.cpfCnpj) ?? [];
    lista.push(c);
    grupos.set(c.cpfCnpj, lista);
  }

  const saida: Array<CadastroSplit & { vigenteEm: string | null; periodos: number }> = [];
  for (const [, lista] of grupos) {
    const ordenada = [...lista].sort((a, b) => a.inicioVigencia.localeCompare(b.inicioVigencia));
    const jaVigentes = ordenada.filter((c) => c.inicioVigencia <= dia);
    const escolhida = jaVigentes.length > 0 ? jaVigentes[jaVigentes.length - 1] : ordenada[0];
    saida.push({
      ...escolhida,
      // Nulo quer dizer "já está valendo na data escolhida". Uma data aqui é o
      // aviso de que aquele percentual só começa depois.
      vigenteEm: jaVigentes.length > 0 ? null : ordenada[0].inicioVigencia,
      periodos: ordenada.length,
    });
  }
  return saida.sort((a, b) => a.cpfCnpj.localeCompare(b.cpfCnpj));
}

// ---------------------------------------------------------------------------
// As bases de cálculo
// ---------------------------------------------------------------------------
/**
 * Os componentes da base, com o rótulo e o SINAL de cada um.
 *
 * O sinal está aqui porque é o que a tela precisa mostrar para a conta fazer
 * sentido: desconto incondicional SUBTRAI, tudo o mais SOMA. Sem ele a tela
 * listaria dez campos e um total que não fecha com a soma visível, e o aluno
 * não teria como conferir — que é o trabalho dele nesta calculadora.
 *
 * A lista e os nomes dos campos são os do gov (OpenAPI de 14/09). Os dois
 * conjuntos são diferentes de propósito: o IS não desconta o próprio IS e tem
 * bonificação e devolução de vendas, que a base do CBS/IBS não tem.
 */
export type Componente = { campo: string; rotulo: string; soma: boolean };

export const COMPONENTES_CBS_IBS: Componente[] = [
  { campo: "valorBem", rotulo: "Valor do bem ou serviço", soma: true },
  { campo: "ajusteAcrescimos", rotulo: "Acréscimos", soma: true },
  { campo: "juros", rotulo: "Juros", soma: true },
  { campo: "multas", rotulo: "Multas", soma: true },
  { campo: "encargos", rotulo: "Encargos", soma: true },
  { campo: "frete", rotulo: "Frete cobrado na operação", soma: true },
  { campo: "impostoSeletivo", rotulo: "Imposto Seletivo", soma: true },
  { campo: "outrosTributos", rotulo: "Outros tributos e preços públicos", soma: true },
  { campo: "demaisImportancias", rotulo: "Demais importâncias cobradas", soma: true },
  { campo: "icms", rotulo: "ICMS", soma: false },
  { campo: "iss", rotulo: "ISS", soma: false },
  { campo: "pis", rotulo: "PIS", soma: false },
  { campo: "pisImportacao", rotulo: "PIS Importação", soma: false },
  { campo: "cofins", rotulo: "COFINS", soma: false },
  { campo: "cofinsImportacao", rotulo: "COFINS Importação", soma: false },
  { campo: "cosip", rotulo: "COSIP", soma: false },
  { campo: "ipi", rotulo: "IPI", soma: false },
  { campo: "descontoIncondicional", rotulo: "Desconto incondicional", soma: false },
];

export const COMPONENTES_IS: Componente[] = [
  { campo: "valorBem", rotulo: "Valor do bem", soma: true },
  { campo: "ajusteAcrescimos", rotulo: "Acréscimos", soma: true },
  { campo: "juros", rotulo: "Juros", soma: true },
  { campo: "multas", rotulo: "Multas", soma: true },
  { campo: "encargos", rotulo: "Encargos", soma: true },
  { campo: "freteCobrado", rotulo: "Frete cobrado na operação", soma: true },
  { campo: "outrosTributos", rotulo: "Outros tributos e preços públicos", soma: true },
  { campo: "demaisImportancias", rotulo: "Demais importâncias cobradas", soma: true },
  { campo: "icms", rotulo: "ICMS", soma: false },
  { campo: "iss", rotulo: "ISS", soma: false },
  { campo: "cosip", rotulo: "COSIP", soma: false },
  { campo: "ipi", rotulo: "IPI", soma: false },
  { campo: "descontoIncondicional", rotulo: "Desconto incondicional", soma: false },
  { campo: "bonificacao", rotulo: "Bonificações no documento fiscal", soma: false },
  { campo: "devolucaoVendas", rotulo: "Devoluções de vendas (art. 418)", soma: false },
];

/**
 * O corpo da base de cálculo: só os campos PREENCHIDOS.
 *
 * Campo em branco não vai como zero. Mandar zero seria afirmar ao gov que o
 * ICMS da operação é zero, e não que o aluno não informou — e `avisos` da base
 * unificada mostra que o gov tem o que dizer sobre campo que não cabe no
 * contexto. O que ele ignora por vigência ele diz; o que a gente inventa, não.
 */
export function corpoDaBase(
  ano: number,
  valores: Record<string, number | null>,
  componentes: Componente[],
): Record<string, unknown> {
  const corpo: Record<string, unknown> = { anoFatoGerador: ano };
  for (const c of componentes) {
    const v = valores[c.campo];
    if (v !== null && v !== undefined) corpo[c.campo] = v;
  }
  return corpo;
}

/** Quanto a tela espera que a base dê, para o aluno conferir a conta do gov. */
export function baseEsperada(
  valores: Record<string, number | null>,
  componentes: Componente[],
): number {
  let total = 0;
  for (const c of componentes) {
    const v = valores[c.campo];
    if (v === null || v === undefined) continue;
    total += c.soma ? v : -v;
  }
  // Centavos por dentro: 0.1 + 0.2 não é 0.3 em ponto flutuante, e esta soma
  // aparece ao lado do número do gov — um centavo de diferença faria o aluno
  // achar que a conta dele está errada.
  return Math.round(total * 100) / 100;
}
